import { useMemo, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import { Card, CardDescription, CardTitle } from '@/components/ui/card';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { ErrorState } from '@/components/common/ErrorState';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useDistressBoard } from '../lib/api';
import type { DistressBoardRowDto } from '../types';
import { FIT_HELP, Flag, SourceLink, fmtDate, flagEmoji, scoreTone } from './_shared';

// Tab — Distress board (Phase B). Active formal insolvency processes grouped by
// jurisdiction, fed by the registry/court pollers. Sorted most-urgent-first: next
// deadline ascending (no-deadline rows last), then filed date descending. Deadlines
// within 14 days render red — those are the ones a bid team can still act on.

const DEADLINE_SOON_DAYS = 14;

const COUNTRY_LABELS: Record<string, string> = {
  NO: 'Norway',
  GB: 'United Kingdom',
  US: 'United States',
  DE: 'Germany',
  SG: 'Singapore',
  AU: 'Australia',
  BR: 'Brazil',
  CA: 'Canada',
  CL: 'Chile',
  MY: 'Malaysia',
  ZA: 'South Africa',
  FR: 'France',
  NL: 'Netherlands',
  ES: 'Spain',
  JE: 'Jersey',
};

/** "in_process" → "In process" — the DTO carries snake_case status strings. */
function humanizeStatus(status: string): string {
  const s = status.replaceAll('_', ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function countryLabel(cc: string): string {
  return COUNTRY_LABELS[cc] ?? cc;
}

/** Whole days from today (local midnight) to an ISO date; negative = past due. */
function daysUntil(iso: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(`${iso.slice(0, 10)}T00:00:00`);
  return Math.round((target.getTime() - today.getTime()) / 86_400_000);
}

/** Next deadline asc (null last), then filed date desc. */
function compareRows(a: DistressBoardRowDto, b: DistressBoardRowDto): number {
  if (a.nextDeadline && b.nextDeadline && a.nextDeadline !== b.nextDeadline)
    return a.nextDeadline.localeCompare(b.nextDeadline);
  if (!a.nextDeadline !== !b.nextDeadline) return a.nextDeadline ? -1 : 1;
  return b.filedDate.localeCompare(a.filedDate);
}

/** Filed-within filter options; null months = no age cutoff. */
const AGE_OPTIONS = [
  { key: '3m', label: '3 months', months: 3 },
  { key: '6m', label: '6 months', months: 6 },
  { key: '12m', label: '12 months', months: 12 },
  { key: 'all', label: 'All', months: null },
] as const;
type AgeKey = (typeof AGE_OPTIONS)[number]['key'];

function agedCutoffIso(months: number): string {
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
}

export default function Distress() {
  const { data, isLoading, error } = useDistressBoard();
  const rows = useMemo(() => [...(data ?? [])].sort(compareRows), [data]);

  // Jurisdiction filter chips — empty selection = all.
  const [selected, setSelected] = useState<string[]>([]);
  // Filed-within filter, default 6 months. Rows with an UPCOMING deadline stay visible
  // regardless of filing age — an old process with a live bid deadline (Edyce: filed
  // 2025-10, creditor vote 2026-07) is exactly what the board must not hide.
  const [age, setAge] = useState<AgeKey>('6m');

  const jurisdictions = useMemo(() => {
    const set = new Set(rows.map((r) => r.jurisdiction));
    return [...set].sort((a, b) => countryLabel(a).localeCompare(countryLabel(b)));
  }, [rows]);

  if (isLoading) return <LoadingSkeleton lines={8} />;
  if (error) return <ErrorState message={(error as Error).message} />;

  const toggle = (cc: string) =>
    setSelected((prev) => (prev.includes(cc) ? prev.filter((x) => x !== cc) : [...prev, cc]));

  const months = AGE_OPTIONS.find((o) => o.key === age)?.months ?? null;
  const cutoff = months === null ? null : agedCutoffIso(months);
  const filtered = rows.filter(
    (r) =>
      (selected.length === 0 || selected.includes(r.jurisdiction)) &&
      (cutoff === null || r.filedDate >= cutoff || !!r.nextDeadline)
  );

  // Group by jurisdiction preserving the global urgency sort — the group whose most
  // urgent deadline comes first leads the board.
  const groups = new Map<string, DistressBoardRowDto[]>();
  for (const r of filtered) {
    const g = groups.get(r.jurisdiction);
    if (g) g.push(r);
    else groups.set(r.jurisdiction, [r]);
  }

  return (
    <div className="stagger">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle>Distress board</CardTitle>
            <CardDescription className="mt-2">
              Active formal insolvency processes across the target universe, grouped by jurisdiction.
              Deadlines within {DEADLINE_SOON_DAYS} days render red — every row links its court or
              registry source.
            </CardDescription>
          </div>
          {rows.length > 0 && (
            /* Same-origin <a> — rides the portal BFF proxy with the session cookie
               (mirrors the dossier docx export button). */
            <Button asChild size="sm">
              <a href="/api/mna/distress-board/calendar.ics" download>
                <CalendarDays className="h-3.5 w-3.5" aria-hidden /> Export deadlines (.ics)
              </a>
            </Button>
          )}
        </div>
      </Card>

      {rows.length === 0 ? (
        <Card>
          <CardDescription>
            No active distress processes — signals arrive via the Phase B registry pollers.
          </CardDescription>
        </Card>
      ) : (
        <>
          <Card>
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">
                Jurisdiction
              </span>
              {jurisdictions.map((cc) => (
                <JurisdictionChip key={cc} cc={cc} active={selected.includes(cc)} onToggle={() => toggle(cc)} />
              ))}
              {selected.length > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected([])}
                  className="text-xs text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent font-(family-name:--font-ui)"
                >
                  Reset
                </button>
              )}
              <span className="ml-auto text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">
                {filtered.length} of {rows.length} processes
              </span>
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-border pt-3">
              <span className="text-xs uppercase tracking-widest text-muted font-(family-name:--font-ui)">
                Filed within
              </span>
              {AGE_OPTIONS.map((o) => (
                <button
                  key={o.key}
                  type="button"
                  aria-pressed={age === o.key}
                  onClick={() => setAge(o.key)}
                  className={cn(
                    'inline-flex items-center rounded-full border px-3 py-1.5',
                    'font-(family-name:--font-ui) text-xs font-medium uppercase tracking-[0.12em] transition-colors',
                    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
                    age === o.key
                      ? 'border-accent bg-surface-elevated text-accent'
                      : 'border-border bg-surface text-muted hover:text-text'
                  )}
                >
                  {o.label}
                </button>
              ))}
              <span className="text-xs text-subtle">
                rows with an upcoming deadline always show
              </span>
            </div>
          </Card>

          <Card>
            {filtered.length === 0 ? (
              <CardDescription>No distress processes match the current filters.</CardDescription>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-accent">
                      <Th>Company</Th>
                      <Th center>Jurisdiction</Th>
                      <Th>Process</Th>
                      <Th>Filed</Th>
                      <Th>Status</Th>
                      <Th>Next deadline</Th>
                      <Th center>Fit</Th>
                      <Th center>Source</Th>
                    </tr>
                  </thead>
                  {[...groups.entries()].map(([cc, groupRows]) => (
                    <tbody key={cc}>
                      <tr>
                        <th
                          scope="rowgroup"
                          colSpan={8}
                          className="pt-5 pb-2 px-3 text-left font-(family-name:--font-ui) text-[0.7rem] font-semibold uppercase tracking-[0.2em] text-muted"
                        >
                          <Flag countryCode={cc} /> {countryLabel(cc)}
                        </th>
                      </tr>
                      {groupRows.map((r) => (
                        <DistressRow key={`${r.companySlug}:${r.filedDate}:${r.processType}`} row={r} />
                      ))}
                    </tbody>
                  ))}
                </table>
              </div>
            )}
          </Card>
        </>
      )}
    </div>
  );
}

/* ---------- pieces ---------- */

function Th({ children, center }: { children: ReactNode; center?: boolean }) {
  return (
    <th
      className={cn(
        'py-2 px-3 text-[0.75rem] uppercase tracking-widest text-muted font-(family-name:--font-ui)',
        center ? 'text-center' : 'text-left'
      )}
    >
      {children}
    </th>
  );
}

function JurisdictionChip({ cc, active, onToggle }: { cc: string; active: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onToggle}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5',
        'font-(family-name:--font-ui) text-xs font-medium uppercase tracking-[0.12em] transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        active
          ? 'border-accent bg-surface-elevated text-accent'
          : 'border-border bg-surface text-muted hover:text-text'
      )}
    >
      <span aria-hidden>{flagEmoji(cc)}</span>
      {countryLabel(cc)}
    </button>
  );
}

const MAX_EXTRA_MILESTONES = 3;

function DeadlineCell({ row: r }: { row: DistressBoardRowDto }) {
  if (!r.nextDeadline) return <span className="text-muted">—</span>;
  const days = daysUntil(r.nextDeadline);
  const soon = days <= DEADLINE_SOON_DAYS;
  // Block 3: the full milestone calendar (soonest first, [0] is the next deadline shown big).
  const upcoming = (r.deadlines ?? []).slice(1);
  const extra = upcoming.slice(0, MAX_EXTRA_MILESTONES);
  const overflow = upcoming.length - extra.length;
  return (
    <span className={cn('[font-variant-numeric:tabular-nums]', soon && 'font-semibold text-accent-danger')}>
      <span className="whitespace-nowrap">
        {fmtDate(r.nextDeadline)}
        {soon && (
          <span className="ml-1.5 rounded-(--radius-sm) border border-accent-danger px-1 py-px font-(family-name:--font-ui) text-[0.6rem] uppercase tracking-[0.1em]">
            {days < 0 ? `${-days}d overdue` : days === 0 ? 'today' : `${days}d`}
          </span>
        )}
      </span>
      {r.nextDeadlineLabel && <span className="block text-xs font-normal text-muted">{r.nextDeadlineLabel}</span>}
      {extra.length > 0 && (
        <span className="mt-1 block text-xs font-normal text-muted">
          {extra.map((d) => (
            <span key={`${d.date}:${d.label}`} className="block">
              {fmtDate(d.date)} — {d.label}
            </span>
          ))}
          {overflow > 0 && <span className="block">+{overflow} more</span>}
        </span>
      )}
    </span>
  );
}

function DistressRow({ row: r }: { row: DistressBoardRowDto }) {
  return (
    <tr className="border-b border-border align-top hover:bg-surface-hover">
      <td className="min-w-[11rem] py-3 px-3">
        <Link
          to={`/mna/companies/${r.companySlug}`}
          className="font-medium text-accent hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          {r.companyName}
        </Link>
      </td>
      <td className="py-3 px-3 text-center">
        <Flag countryCode={r.jurisdiction} />
      </td>
      {/* NO nowrap — backfill process types are verbose ("Reorganización judicial (Chile,
          Ley 20.720)"); nowrap blew the table past the card width and scrolled the company
          column out of view. Wrap, but keep the column bounded. */}
      <td className="max-w-[14rem] py-3 px-3">{r.processType}</td>
      <td className="py-3 px-3 whitespace-nowrap [font-variant-numeric:tabular-nums]">{fmtDate(r.filedDate)}</td>
      <td className="py-3 px-3 whitespace-nowrap text-muted">{humanizeStatus(r.status)}</td>
      <td className="max-w-[18rem] py-3 px-3">
        <DeadlineCell row={r} />
      </td>
      <td title={FIT_HELP} className="cursor-help py-3 px-3 text-center font-(family-name:--font-ui) [font-variant-numeric:tabular-nums]">
        {r.fitScore === null ? (
          <span className="text-muted">—</span>
        ) : (
          <span className={cn('font-semibold', scoreTone(r.fitScore))}>{r.fitScore}</span>
        )}
      </td>
      <td className="py-3 px-3 text-center">
        {r.source ? <SourceLink source={r.source} /> : <span className="text-muted">—</span>}
      </td>
    </tr>
  );
}
